import AppHeader from '../../components/AppHeader';
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,
  Alert,
  CircularProgress
} from '@mui/material';
import { ArrowBack, ArrowForward, Save } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SignatureCanvas from '../../components/SignatureCanvas';
import ImageUploader from '../../components/ImageUploader';

// Helper function to capitalize names with hyphens
const capitalizeNameWithHyphens = (text) => {
  if (!text) return '';
  return text
    .split(/(\s+|-)/g)
    .map((part) => {
      if (part === ' ' || part === '-') return part;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join('');
};

// Auto-format Auftragsnummer: 4 digits / 2-digit year
const formatAuftragsnummer = (value) => {
  const cleaned = value.replace(/[^\d/]/g, '');
  const parts = cleaned.split('/');
  
  if (parts.length === 1 && parts[0].length >= 4) {
    const digits = parts[0].slice(0, 4);
    const year = new Date().getFullYear().toString().slice(-2);
    return `${digits}/${year}`;
  }
  
  if (parts.length === 2) {
    const digits = parts[0].slice(0, 4);
    const yearPart = parts[1].slice(0, 2);
    return yearPart ? `${digits}/${yearPart}` : `${digits}/`;
  }
  
  return cleaned.slice(0, 7);
};

const steps = ['Kundendaten', 'Anlagendetails', 'Prüfungen & Messwerte', 'Bilder & Zufriedenheit', 'Unterschriften'];

export default function WaermepumpeForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const customer = location.state?.customer;

  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    // Kundendaten
    kunde: customer?.name || '',
    kunde_strasse: customer?.strasse || '',
    kunde_plz: customer?.plz || '',
    kunde_ort: customer?.ort || '',
    kunde_email: customer?.email || '',
    standort: '',
    auftrag: '',
    datum: new Date().toISOString().split('T')[0],
    anlagen_id: '',
    monteur: user?.full_name || user?.username || '',
    
    // WP-Typ (NEU)
    wp_typ: 'split', // 'split' oder 'monoblock'
    
    // Anlagendetails
    hersteller: '',
    hersteller_manuell: '',
    nenn_heizleistung: '',
    wp_innengeraet: {
      modell: '',
      seriennummer: ''
    },
    wp_aussengeraet: {
      modell: '',
      seriennummer: ''
    },
    
    // Messwerte
    wp_messwerte: {
      wasser_vorlauftemperatur: '',
      wasser_ruecklauftemperatur: '',
      wasserdurchfluss: '', // in m³/h
      wasserdurchfluss_lh: '', // in L/h
      berechnete_heizleistung: '', // wird berechnet in L/h
      berechnete_heizleistung_kw: '', // Heizleistung in kW
      leistung_prozent: '', // Prozent der Nennleistung
      leistung_status: '', // gut/mittel/schlecht
      heizleistung_ok: true
    },
    
    // Prüfungen
    checklist_wp_aussen: {
      waermetauscher_pruefen_reinigen: false,
      kaeltetechnische_anschluesse_pruefen: false,
      kondensatwanne_ablauf_pruefen: false,
      kondensatpumpe_pruefen_entlueften: false,
      elektrische_anschluesse_pruefen: false,
      schalldaemmung_pruefen: false,
      ventilator_pruefen_reinigen: false,
      geraetefuesse_befestigung_pruefen: false,
      rohrleitungen_daemmung_pruefen: false,
      sonstiges_aussen: ''
    },
    checklist_wp_innen: {
      wasserdruck_pruefen_nachfuellen: false,
      wasserfilter_reinigen: false,
      ausdehnungsgefaess_pruefen: false,
      sicherheitsventil_pruefen: false,
      mischer_pruefen: false,
      umwaelzpumpe_pruefen: false,
      hydraulische_anschluesse_pruefen: false,
      elektrische_anschluesse_pruefen_innen: false,
      regelung_bedienung_pruefen: false,
      sonstiges_innen: ''
    },
    
    // Bilder & Zufriedenheit
    bilder: [],
    kunde_zufrieden: true,
    kunde_unzufrieden_grund: '',
    zusaetzliche_arbeiten: '',
    hinweise: '',
    
    // Unterschriften
    unterschrift_kunde: '',
    unterschrift_monteur: '',
    unterschrift_kunde_name: ''
  });

  const handleChange = (field, value) => {
    if (field.startsWith('wp_innengeraet.')) {
      const subfield = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        wp_innengeraet: {
          ...prev.wp_innengeraet,
          [subfield]: value
        }
      }));
    } else if (field.startsWith('wp_aussengeraet.')) {
      const subfield = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        wp_aussengeraet: {
          ...prev.wp_aussengeraet,
          [subfield]: value
        }
      }));
    } else if (field.startsWith('wp_messwerte.')) {
      const subfield = field.split('.')[1];
      setFormData(prev => {
        const newMesswerte = {
          ...prev.wp_messwerte,
          [subfield]: value
        };
        
        // Auto-Berechnung des Volumenstrom in L/h
        if (['wasser_vorlauftemperatur', 'wasser_ruecklauftemperatur', 'wasserdurchfluss'].includes(subfield)) {
          const vorlauf = parseFloat(newMesswerte.wasser_vorlauftemperatur) || 0;
          const ruecklauf = parseFloat(newMesswerte.wasser_ruecklauftemperatur) || 0;
          const durchfluss = parseFloat(newMesswerte.wasserdurchfluss) || 0;
          
          // Berechnung der aktuellen Heizleistung in kW
          // Q (kW) = V (m³/h) × ΔT × 1.163
          if (vorlauf > 0 && ruecklauf > 0 && durchfluss > 0) {
            const deltaT = vorlauf - ruecklauf;
            const heizleistungKW = durchfluss * deltaT * 1.163;
            
            // Speichere berechnete Heizleistung in kW und L/h
            newMesswerte.berechnete_heizleistung_kw = heizleistungKW.toFixed(2);
            newMesswerte.berechnete_heizleistung = (durchfluss * 1000).toFixed(0); // m³/h zu L/h
            
            // Berechne Prozentsatz der Nennleistung
            const nennleistung = parseFloat(prev.nenn_heizleistung) || 0;
            if (nennleistung > 0) {
              const prozentsatz = (heizleistungKW / nennleistung) * 100;
              newMesswerte.leistung_prozent = prozentsatz.toFixed(1);
              
              // Bestimme Status basierend auf Prozentsatz
              if (prozentsatz >= 90) {
                newMesswerte.leistung_status = 'gut'; // grün
              } else if (prozentsatz >= 75) {
                newMesswerte.leistung_status = 'mittel'; // orange
              } else {
                newMesswerte.leistung_status = 'schlecht'; // rot
              }
            }
          }
        }
        
        return {
          ...prev,
          wp_messwerte: newMesswerte
        };
      });
    } else if (field.startsWith('checklist_wp_aussen.')) {
      const subfield = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        checklist_wp_aussen: {
          ...prev.checklist_wp_aussen,
          [subfield]: value
        }
      }));
    } else if (field.startsWith('checklist_wp_innen.')) {
      const subfield = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        checklist_wp_innen: {
          ...prev.checklist_wp_innen,
          [subfield]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleNext = () => {
    // Validation per step
    if (activeStep === 0) {
      if (!formData.auftrag) {
        setError('Auftragsnummer ist ein Pflichtfeld');
        return;
      }
      const auftragPattern = /^\d{4}\/\d{2}$/;
      if (!auftragPattern.test(formData.auftrag)) {
        setError('Auftragsnummer muss im Format "1234/24" sein (4 Ziffern / 2-stelliges Jahr)');
        return;
      }
      if (!formData.kunde) {
        setError('Bitte Kundenname ausfüllen');
        return;
      }
    }

    if (activeStep === 1) {
      if (!formData.nenn_heizleistung) {
        setError('Bitte Nenn-Heizleistung eingeben');
        return;
      }
      
      // Bei Split: Innen- UND Außengerät Pflicht
      if (formData.wp_typ === 'split') {
        if (!formData.wp_innengeraet.modell || !formData.wp_aussengeraet.modell) {
          setError('Bitte Modellnummern für Innen- und Außengerät eingeben');
          return;
        }
      }
      
      // Bei Monoblock: NUR Außengerät Pflicht
      if (formData.wp_typ === 'monoblock') {
        if (!formData.wp_aussengeraet.modell) {
          setError('Bitte Modellnummer für Außengerät/Monoblock eingeben');
          return;
        }
      }
    }

    if (activeStep === 4) {
      if (!formData.unterschrift_monteur || !formData.unterschrift_kunde) {
        setError('Beide Unterschriften sind erforderlich');
        return;
      }
    }

    setError('');
    if (activeStep < steps.length - 1) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setError('');
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Kundendaten
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Kundendaten
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Kunde"
                  value={formData.kunde}
                  onChange={(e) => {
                    const capitalized = capitalizeNameWithHyphens(e.target.value);
                    handleChange('kunde', capitalized);
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Straße"
                  value={formData.kunde_strasse}
                  onChange={(e) => {
                    const capitalized = capitalizeNameWithHyphens(e.target.value);
                    handleChange('kunde_strasse', capitalized);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="PLZ"
                  value={formData.kunde_plz}
                  onChange={(e) => handleChange('kunde_plz', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Ort"
                  value={formData.kunde_ort}
                  onChange={(e) => {
                    const capitalized = capitalizeNameWithHyphens(e.target.value);
                    handleChange('kunde_ort', capitalized);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Standort/Gerätestandort"
                  value={formData.standort}
                  onChange={(e) => {
                    const capitalized = capitalizeNameWithHyphens(e.target.value);
                    handleChange('standort', capitalized);
                  }}
                  helperText="z.B. Wohnzimmer, Büro, etc."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Auftragsnummer"
                  value={formData.auftrag}
                  onChange={(e) => {
                    const formatted = formatAuftragsnummer(e.target.value);
                    handleChange('auftrag', formatted);
                  }}
                  required
                  placeholder="1234/24"
                  helperText="Format: 1234/24 (4 Ziffern / 2-stelliges Jahr)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Datum"
                  type="date"
                  value={formData.datum}
                  onChange={(e) => handleChange('datum', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Anlagen-ID"
                  value={formData.anlagen_id}
                  onChange={(e) => handleChange('anlagen_id', e.target.value.toUpperCase())}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Monteur"
                  value={formData.monteur}
                  onChange={(e) => handleChange('monteur', e.target.value)}
                  helperText="Automatisch aus Benutzerprofil"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1: // Anlagendetails
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Anlagendetails - Wärmepumpe
            </Typography>

            <Grid container spacing={3}>
              {/* Hersteller - eigene Zeile */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Hersteller *</InputLabel>
                  <Select
                    value={formData.hersteller === 'Sonstige' || ['Panasonic', 'Kaysun', 'Mitsubishi', 'Daikin', 'Rotex', 'Viessmann'].includes(formData.hersteller) ? formData.hersteller : 'Sonstige'}
                    onChange={(e) => {
                      if (e.target.value === 'Sonstige') {
                        handleChange('hersteller', '');
                      } else {
                        handleChange('hersteller', e.target.value);
                      }
                    }}
                    label="Hersteller *"
                  >
                    <MenuItem value="Panasonic">Panasonic</MenuItem>
                    <MenuItem value="Kaysun">Kaysun</MenuItem>
                    <MenuItem value="Mitsubishi">Mitsubishi</MenuItem>
                    <MenuItem value="Daikin">Daikin</MenuItem>
                    <MenuItem value="Rotex">Rotex</MenuItem>
                    <MenuItem value="Viessmann">Viessmann</MenuItem>
                    <MenuItem value="Sonstige">Sonstiger Hersteller</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Sonstiger Hersteller */}
              {formData.hersteller === 'Sonstige' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Hersteller (manuell eingeben) *"
                    value={formData.hersteller_manuell}
                    onChange={(e) => {
                      handleChange('hersteller_manuell', e.target.value);
                      handleChange('hersteller', e.target.value);
                    }}
                    placeholder="Hersteller eingeben"
                  />
                </Grid>
              )}
              
              {/* Nenn-Heizleistung - eigene Zeile */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nenn-Heizleistung (kW) *"
                  type="number"
                  value={formData.nenn_heizleistung}
                  onChange={(e) => handleChange('nenn_heizleistung', e.target.value)}
                  inputProps={{ step: 0.1, min: 0 }}
                  helperText="Nennleistung der Wärmepumpe in kW"
                />
              </Grid>

              {/* Radiobutton WP-Typ */}
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Wärmepumpen-Typ *</FormLabel>
                  <RadioGroup
                    row
                    value={formData.wp_typ}
                    onChange={(e) => handleChange('wp_typ', e.target.value)}
                  >
                    <FormControlLabel value="split" control={<Radio />} label="Split-Wärmepumpe" />
                    <FormControlLabel value="monoblock" control={<Radio />} label="Monoblock" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Innengerät (nur bei Split) */}
              {formData.wp_typ === 'split' && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
                      Innengerät
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Modellnummer *"
                      value={formData.wp_innengeraet.modell}
                      onChange={(e) => handleChange('wp_innengeraet.modell', e.target.value)}
                      placeholder="z.B. AQUAREA WH-SXC12H9E8"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Seriennummer"
                      value={formData.wp_innengeraet.seriennummer}
                      onChange={(e) => handleChange('wp_innengeraet.seriennummer', e.target.value)}
                    />
                  </Grid>
                </>
              )}

              {/* Außengerät */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
                  {formData.wp_typ === 'monoblock' ? 'Monoblock' : 'Außengerät'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Modellnummer *"
                  value={formData.wp_aussengeraet.modell}
                  onChange={(e) => handleChange('wp_aussengeraet.modell', e.target.value)}
                  placeholder={formData.wp_typ === 'monoblock' ? 'z.B. AQUAREA WH-MXC12H9E8' : 'z.B. AQUAREA WH-UX12HE8'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Seriennummer"
                  value={formData.wp_aussengeraet.seriennummer}
                  onChange={(e) => handleChange('wp_aussengeraet.seriennummer', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 2: // Prüfungen & Messwerte
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Messwerte
            </Typography>
            
            <Grid container spacing={2}>
              {/* Vorlauftemperatur - eigene Zeile */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Wasser Vorlauftemperatur (°C)"
                  type="number"
                  value={formData.wp_messwerte.wasser_vorlauftemperatur}
                  onChange={(e) => handleChange('wp_messwerte.wasser_vorlauftemperatur', e.target.value)}
                  inputProps={{ step: 0.1 }}
                />
              </Grid>
              
              {/* Rücklauftemperatur - eigene Zeile */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Wasser Rücklauftemperatur (°C)"
                  type="number"
                  value={formData.wp_messwerte.wasser_ruecklauftemperatur}
                  onChange={(e) => handleChange('wp_messwerte.wasser_ruecklauftemperatur', e.target.value)}
                  inputProps={{ step: 0.1 }}
                />
              </Grid>
              
              {/* Wasserdurchfluss m³/h - eigene Zeile */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Wasserdurchfluss (m³/h)"
                  type="number"
                  value={formData.wp_messwerte.wasserdurchfluss}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleChange('wp_messwerte.wasserdurchfluss', value);
                    // Automatische Umrechnung zu L/h
                    if (value) {
                      const lh = parseFloat(value) * 1000;
                      handleChange('wp_messwerte.wasserdurchfluss_lh', lh.toFixed(0));
                    }
                  }}
                  inputProps={{ step: 0.01 }}
                  helperText="Durchfluss in Kubikmeter pro Stunde"
                />
              </Grid>
              
              {/* ODER Wasserdurchfluss L/h - eigene Zeile */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Wasserdurchfluss (L/h)"
                  type="number"
                  value={formData.wp_messwerte.wasserdurchfluss_lh || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleChange('wp_messwerte.wasserdurchfluss_lh', value);
                    // Automatische Umrechnung zu m³/h
                    if (value) {
                      const m3h = parseFloat(value) / 1000;
                      handleChange('wp_messwerte.wasserdurchfluss', m3h.toFixed(3));
                    }
                  }}
                  inputProps={{ step: 1 }}
                  helperText="Durchfluss in Liter pro Stunde (wird automatisch aus m³/h berechnet)"
                />
              </Grid>
              
              {/* Berechneter Volumenstrom - eigene Zeile */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Berechneter Volumenstrom (L/h)"
                  value={formData.wp_messwerte.berechnete_heizleistung || ''}
                  InputProps={{ 
                    readOnly: true,
                    style: { fontWeight: 'bold', fontSize: '1.1rem' }
                  }}
                  helperText="Wird automatisch berechnet"
                />
              </Grid>
              
              {/* Dot + Prozent - eigene Zeile */}
              {formData.wp_messwerte.leistung_prozent && (
                <Grid item xs={12}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    p: 2, 
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1 
                  }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: 
                          formData.wp_messwerte.leistung_status === 'gut' ? '#4caf50' :
                          formData.wp_messwerte.leistung_status === 'mittel' ? '#ff9800' :
                          '#f44336',
                        flexShrink: 0
                      }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {formData.wp_messwerte.leistung_prozent}% der Nennleistung
                      </Typography>
                      {formData.wp_messwerte.berechnete_heizleistung_kw && (
                        <Typography variant="body2" color="text.secondary">
                          Heizleistung: {formData.wp_messwerte.berechnete_heizleistung_kw} kW
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Checkliste Außengerät
            </Typography>
            
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_aussen.waermetauscher_pruefen_reinigen}
                      onChange={(e) => handleChange('checklist_wp_aussen.waermetauscher_pruefen_reinigen', e.target.checked)}
                    />
                  }
                  label="Wärmetauscher prüfen / reinigen"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_aussen.kaeltetechnische_anschluesse_pruefen}
                      onChange={(e) => handleChange('checklist_wp_aussen.kaeltetechnische_anschluesse_pruefen', e.target.checked)}
                    />
                  }
                  label="Kältetechnische Anschlüsse prüfen"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_aussen.kondensatwanne_ablauf_pruefen}
                      onChange={(e) => handleChange('checklist_wp_aussen.kondensatwanne_ablauf_pruefen', e.target.checked)}
                    />
                  }
                  label="Kondensatwanne / Ablauf prüfen"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_aussen.kondensatpumpe_pruefen_entlueften}
                      onChange={(e) => handleChange('checklist_wp_aussen.kondensatpumpe_pruefen_entlueften', e.target.checked)}
                    />
                  }
                  label="Kondensatpumpe prüfen / entlüften"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_aussen.elektrische_anschluesse_pruefen}
                      onChange={(e) => handleChange('checklist_wp_aussen.elektrische_anschluesse_pruefen', e.target.checked)}
                    />
                  }
                  label="Elektrische Anschlüsse prüfen"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_aussen.schalldaemmung_pruefen}
                      onChange={(e) => handleChange('checklist_wp_aussen.schalldaemmung_pruefen', e.target.checked)}
                    />
                  }
                  label="Schalldämmung prüfen"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_aussen.ventilator_pruefen_reinigen}
                      onChange={(e) => handleChange('checklist_wp_aussen.ventilator_pruefen_reinigen', e.target.checked)}
                    />
                  }
                  label="Ventilator prüfen / reinigen"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_aussen.geraetefuesse_befestigung_pruefen}
                      onChange={(e) => handleChange('checklist_wp_aussen.geraetefuesse_befestigung_pruefen', e.target.checked)}
                    />
                  }
                  label="Gerätefüße / Befestigung prüfen"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_aussen.rohrleitungen_daemmung_pruefen}
                      onChange={(e) => handleChange('checklist_wp_aussen.rohrleitungen_daemmung_pruefen', e.target.checked)}
                    />
                  }
                  label="Rohrleitungen / Dämmung prüfen"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Sonstiges (Außengerät)"
                  multiline
                  rows={2}
                  value={formData.checklist_wp_aussen.sonstiges_aussen}
                  onChange={(e) => handleChange('checklist_wp_aussen.sonstiges_aussen', e.target.value)}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Checkliste Innengerät / Hydraulik
            </Typography>
            
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_innen.wasserdruck_pruefen_nachfuellen}
                      onChange={(e) => handleChange('checklist_wp_innen.wasserdruck_pruefen_nachfuellen', e.target.checked)}
                    />
                  }
                  label="Wasserdruck prüfen / nachfüllen"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_innen.wasserfilter_reinigen}
                      onChange={(e) => handleChange('checklist_wp_innen.wasserfilter_reinigen', e.target.checked)}
                    />
                  }
                  label="Wasserfilter reinigen"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_innen.ausdehnungsgefaess_pruefen}
                      onChange={(e) => handleChange('checklist_wp_innen.ausdehnungsgefaess_pruefen', e.target.checked)}
                    />
                  }
                  label="Ausdehnungsgefäß prüfen"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_innen.sicherheitsventil_pruefen}
                      onChange={(e) => handleChange('checklist_wp_innen.sicherheitsventil_pruefen', e.target.checked)}
                    />
                  }
                  label="Sicherheitsventil prüfen"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_innen.mischer_pruefen}
                      onChange={(e) => handleChange('checklist_wp_innen.mischer_pruefen', e.target.checked)}
                    />
                  }
                  label="Mischer prüfen"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_innen.umwaelzpumpe_pruefen}
                      onChange={(e) => handleChange('checklist_wp_innen.umwaelzpumpe_pruefen', e.target.checked)}
                    />
                  }
                  label="Umwälzpumpe prüfen"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_innen.hydraulische_anschluesse_pruefen}
                      onChange={(e) => handleChange('checklist_wp_innen.hydraulische_anschluesse_pruefen', e.target.checked)}
                    />
                  }
                  label="Hydraulische Anschlüsse prüfen"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_innen.elektrische_anschluesse_pruefen_innen}
                      onChange={(e) => handleChange('checklist_wp_innen.elektrische_anschluesse_pruefen_innen', e.target.checked)}
                    />
                  }
                  label="Elektrische Anschlüsse prüfen"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.checklist_wp_innen.regelung_bedienung_pruefen}
                      onChange={(e) => handleChange('checklist_wp_innen.regelung_bedienung_pruefen', e.target.checked)}
                    />
                  }
                  label="Regelung / Bedienung prüfen"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Sonstiges (Innengerät / Hydraulik)"
                  multiline
                  rows={2}
                  value={formData.checklist_wp_innen.sonstiges_innen}
                  onChange={(e) => handleChange('checklist_wp_innen.sonstiges_innen', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 3: // Bilder & Zufriedenheit
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Bilder
            </Typography>
            
            <ImageUploader
              images={formData.bilder}
              onImagesChange={(images) => handleChange('bilder', images)}
            />

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Hinweise und zusätzliche Arbeiten
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Hinweise"
                  multiline
                  rows={3}
                  value={formData.hinweise}
                  onChange={(e) => handleChange('hinweise', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Zusätzlich durchgeführte Arbeiten"
                  multiline
                  rows={3}
                  value={formData.zusaetzliche_arbeiten}
                  onChange={(e) => handleChange('zusaetzliche_arbeiten', e.target.value)}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Kundenzufriedenheit
            </Typography>
            
            <FormControl component="fieldset">
              <FormLabel component="legend">Ist der Kunde mit der Arbeit zufrieden?</FormLabel>
              <RadioGroup
                row
                value={formData.kunde_zufrieden}
                onChange={(e) => handleChange('kunde_zufrieden', e.target.value === 'true')}
              >
                <FormControlLabel value="true" control={<Radio />} label="Ja" />
                <FormControlLabel value="false" control={<Radio />} label="Nein" />
              </RadioGroup>
            </FormControl>
            
            {!formData.kunde_zufrieden && (
              <TextField
                fullWidth
                label="Grund für Unzufriedenheit"
                multiline
                rows={3}
                value={formData.kunde_unzufrieden_grund}
                onChange={(e) => handleChange('kunde_unzufrieden_grund', e.target.value)}
                sx={{ mt: 2 }}
              />
            )}
          </Box>
        );

      case 4: // Unterschriften
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Unterschriften
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Unterschrift Monteur
                </Typography>
                <SignatureCanvas
                  onSave={(signature) => handleChange('unterschrift_monteur', signature)}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name des Kunden"
                  value={formData.unterschrift_kunde_name}
                  onChange={(e) => handleChange('unterschrift_kunde_name', e.target.value)}
                  helperText="Bitte Name des unterzeichnenden Kunden eingeben"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Unterschrift Kunde
                </Typography>
                <SignatureCanvas
                  onSave={(signature) => handleChange('unterschrift_kunde', signature)}
                />
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Wartungsprotokoll Wärmepumpe
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {renderStepContent()}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              startIcon={<ArrowBack />}
            >
              ZURÜCK
            </Button>
            
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => {
                  // TODO: Submit form
                  navigate('/protocol/summary', { state: { formData, protocolType: 'Wartungsprotokoll Wärmepumpe' } });
                }}
                disabled={loading}
                endIcon={loading ? <CircularProgress size={20} /> : <Save />}
              >
                PROTOKOLL ERSTELLEN
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForward />}
              >
                WEITER
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}